"use client";
import GlobalForm from "@/components/forms/GlobalForm";
import useModalScrollLock from "@/hooks/useModalScrollLock";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";
import GCCForm from "@/components/forms/GCCForm";
import EnqueryForm from "@/components/forms/EnqueryForm";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { googleTagManager } from "@/components/common/utils";
import NursingForm from "@/components/forms/NursingForm";

interface StickyFooterLabelProps {
  pageName?:
    | "CPA"
    | "CMA"
    | "CAIRA"
    | "MPACT2025"
    | "CPA/CMA"
    | "USP"
    | "EA"
    | "Accounting";
}

const StickyFooterLabel: React.FC<StickyFooterLabelProps> = ({ pageName }) => {
  const pathname = usePathname();
  const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);
  const [isWebinarModalOpen, setIsWebinarModalOpen] = useState<boolean>(false);
  const [isFooterFixed, setIsFooterFixed] = useState<boolean>(true);
  const [showDownloadModal] = useState<boolean>(false);

  // Check if current page is the special fees page
  const isFeesPage =
    pathname === "/best-us-cpa-admission-online-course-fees-in-india";

  // Check if current page is the CMA landing page
  const isCmaLandingPage = pathname === "/best-us-cma-coaching-in-india";

  // Check if scroll functionality should be enabled (fees page or CMA landing page)
  const shouldUseScrollFunctionality = isFeesPage || isCmaLandingPage;

  // Helper function to detect course type from URL
  const detectCourseFromUrl = (): {
    course: string;
    courseId: number;
    isAccountingCourse: boolean;
  } => {
    const path = pathname?.toLowerCase() || "";

    const accountingCourses: Record<
      string,
      { course: string; courseId: number }
    > = {
      cpa: { course: "CPA", courseId: 1 },
      cma: { course: "CMA", courseId: 2 },
      usp: { course: "USP", courseId: 3 },
      ea: { course: "EA", courseId: 19 },
      caira: { course: "CAIRA", courseId: 18 },
    };

    const otherCourses: Record<string, { course: string; courseId: number }> = {
      nursing: { course: "Nursing", courseId: 34 },
      mbbs: { course: "MBBS", courseId: 33 },
      mba: { course: "MBA", courseId: 52 },
    };

    for (const [key, value] of Object.entries(accountingCourses)) {
      if (
        path.includes(`/${key}`) ||
        path.includes(`-${key}-`) ||
        path.includes(`-${key}`)
      ) {
        return { ...value, isAccountingCourse: true };
      }
    }

    for (const [key, value] of Object.entries(otherCourses)) {
      if (
        path.includes(`/${key}`) ||
        path.includes(`-${key}-`) ||
        path.includes(`-${key}`)
      ) {
        return { ...value, isAccountingCourse: false };
      }
    }

    return { course: "CPA", courseId: 1, isAccountingCourse: true };
  };

  const {
    course: detectedCourse,
    courseId: detectedCourseId,
    isAccountingCourse,
  } = detectCourseFromUrl();

  // Check if current page is CPA or CMA related
  const isCpaPage = pathname?.startsWith("/cpa");
  const isCmaPage = pathname?.startsWith("/cma");
  const showLoginButton = isCpaPage || isCmaPage;
  const loginUrl = isCpaPage
    ? "https://cpa.mileseducation.com/"
    : isCmaPage
      ? "https://cma.mileseducation.com/"
      : "#";

  // GTM event mappings for sticky footer buttons
  const enquireNowGtmEventMap: Record<string, string> = {
    "/miles-us-cpa-miles-us-cma": "Brand_LP_F_EnquireNow",
    "/best-us-cpa-admission-online-course-fees-in-india": "CPA_LP_F_EnquireNow",
    "/best-us-cma-coaching-in-india": "CMA_LP_F_EnquireNow",
    "/miles-education-cpa-fees-miles-education-cma": "Webinar_LP_F_EnquireNow",
  };

  const registerWebinarGtmEventMap: Record<string, string> = {
    "/miles-us-cpa-miles-us-cma": "Brand_LP_F_RegisterForWebinar",
    "/best-us-cpa-admission-online-course-fees-in-india":
      "CPA_LP_F_RegisterForWebinar",
    "/best-us-cma-coaching-in-india": "CMA_LP_F_RegisterForWebinar",
    "/miles-education-cpa-fees-miles-education-cma":
      "Webinar_LP_F_RegisterForWebinar",
  };

  useEffect(() => {
    document.body.style.overflow = "unset";
    let ticking = false;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const distanceFromBottom =
            documentHeight - (scrollPosition + windowHeight);
          const shouldBeFixed = distanceFromBottom > 20;
          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }
          debounceTimer = setTimeout(() => {
            setIsFooterFixed(shouldBeFixed);
          }, 50);

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, []);

  const handleFormModalOpen = () => {
    const eventName = enquireNowGtmEventMap[pathname];
    if (eventName) {
      googleTagManager(eventName, {
        button_location: "sticky_footer",
        page_url: typeof window !== "undefined" ? window.location.href : "",
      });
    }
    sessionStorage.setItem("modal-open", "true");
    setIsFormModalOpen(true);
  };

  const handleFormModalClose = () => {
    sessionStorage.removeItem("modal-open");
    setIsFormModalOpen(false);
  };

  const handleWebinarModalOpen = () => {
    const eventName = registerWebinarGtmEventMap[pathname];
    if (eventName) {
      googleTagManager(eventName, {
        button_location: "sticky_footer",
        page_url: typeof window !== "undefined" ? window.location.href : "",
      });
    }
    sessionStorage.setItem("modal-open", "true");
    setIsWebinarModalOpen(true);
  };

  const handleWebinarModalClose = () => {
    sessionStorage.removeItem("modal-open");
    setIsWebinarModalOpen(false);
  };

  // Special handlers for fees page
  // Handler for "Join FREE Webinar" button - scrolls to Live Webinar form
  const handleScrollToLiveWebinarForm = () => {
    // Find the live webinar form
    const liveWebinarForm = document.getElementById("live-webinar-form");

    if (liveWebinarForm) {
      // Scroll to the form with some offset
      const yOffset = -100; // Offset from top
      const y =
        liveWebinarForm.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });

      // After scrolling, highlight the form
      setTimeout(() => {
        // Reset any existing styles first to ensure animation works every time
        liveWebinarForm.style.border = "";
        liveWebinarForm.style.boxShadow = "";
        liveWebinarForm.style.transform = "";
        liveWebinarForm.style.transition = "";
        liveWebinarForm.style.backgroundColor = "";

        // Store original styles
        const originalBorder = liveWebinarForm.style.border;
        const originalBackground = liveWebinarForm.style.backgroundColor;

        // Force a reflow to restart the animation
        void liveWebinarForm.offsetWidth;

        // Add pulsing highlight effect with inline styles
        let pulseCount = 0;
        const pulseInterval = setInterval(() => {
          if (pulseCount % 2 === 0) {
            liveWebinarForm.style.border = "3px solid #3399FF";
            liveWebinarForm.style.boxShadow =
              "0 0 30px 15px rgba(51, 153, 255, 0.5), inset 0 0 20px rgba(51, 153, 255, 0.2)";
            liveWebinarForm.style.backgroundColor = "rgba(51, 153, 255, 0.15)";
            liveWebinarForm.style.transform = "scale(1.02)";
            liveWebinarForm.style.transition = "all 0.5s ease-in-out";
          } else {
            liveWebinarForm.style.border = "3px solid #3399FF";
            liveWebinarForm.style.boxShadow =
              "0 0 10px 5px rgba(51, 153, 255, 0.3)";
            liveWebinarForm.style.backgroundColor = "rgba(51, 153, 255, 0.05)";
            liveWebinarForm.style.transform = "scale(1)";
          }
          pulseCount++;

          if (pulseCount >= 6) {
            // 3 complete pulses (6 half-cycles)
            clearInterval(pulseInterval);
            // Restore original styles
            liveWebinarForm.style.border = originalBorder;
            liveWebinarForm.style.boxShadow = "";
            liveWebinarForm.style.backgroundColor = originalBackground;
            liveWebinarForm.style.transform = "";
            liveWebinarForm.style.transition = "";
          }
        }, 500); // 500ms per half-cycle = 1s per complete pulse
      }, 800); // Wait for scroll to complete
    }
  };

  // Handler for "Request a Call" button - scrolls to Banner form
  const handleScrollToBannerForm = () => {
    // Scroll to top of page where the banner form is
    window.scrollTo({ top: 0, behavior: "smooth" });

    // After scrolling, highlight the banner form
    setTimeout(() => {
      // Find the banner form container
      const bannerForm = document.getElementById("banner-form") as HTMLElement;

      if (bannerForm) {
        // Reset any existing styles first to ensure animation works every time
        bannerForm.style.border = "";
        bannerForm.style.boxShadow = "";
        bannerForm.style.transform = "";
        bannerForm.style.transition = "";
        bannerForm.style.backgroundColor = "";

        // Store original styles
        const originalBorder = bannerForm.style.border;
        const originalBackground = bannerForm.style.backgroundColor;

        // Force a reflow to restart the animation
        void bannerForm.offsetWidth;

        // Add pulsing highlight effect with inline styles
        let pulseCount = 0;
        const pulseInterval = setInterval(() => {
          if (pulseCount % 2 === 0) {
            bannerForm.style.border = "3px solid #3399FF";
            bannerForm.style.boxShadow =
              "0 0 30px 15px rgba(51, 153, 255, 0.5), inset 0 0 20px rgba(51, 153, 255, 0.2)";
            bannerForm.style.backgroundColor = "rgba(51, 153, 255, 0.15)";
            bannerForm.style.transform = "scale(1.02)";
            bannerForm.style.transition = "all 0.5s ease-in-out";
          } else {
            bannerForm.style.border = "3px solid #3399FF";
            bannerForm.style.boxShadow = "0 0 10px 5px rgba(51, 153, 255, 0.3)";
            bannerForm.style.backgroundColor = "rgba(51, 153, 255, 0.05)";
            bannerForm.style.transform = "scale(1)";
          }
          pulseCount++;

          if (pulseCount >= 6) {
            // 3 complete pulses (6 half-cycles)
            clearInterval(pulseInterval);
            // Restore original styles
            bannerForm.style.border = originalBorder;
            bannerForm.style.boxShadow = "";
            bannerForm.style.backgroundColor = originalBackground;
            bannerForm.style.transform = "";
            bannerForm.style.transition = "";
          }
        }, 500); // 500ms per half-cycle = 1s per complete pulse
      }
    }, 800); // Wait for scroll to complete
  };

  useModalScrollLock(
    isFormModalOpen || isWebinarModalOpen || showDownloadModal,
    {
      restoreScrollPosition: true,
      preventTouchScroll: true,
    },
  );

  return (
    <>
      <div
        style={{
          background:
            pageName === "CAIRA"
              ? "linear-gradient(90deg, #04101C 0%, #1471E2 100%)"
              : "#071B30",
          boxShadow: "0 0 20px 0 rgba(51, 153, 255, 0.24)",
          transition: "opacity 0.3s ease-in-out",
          contain: "layout style",
        }}
        className={`${isFooterFixed ? "fixed bottom-0 left-0 w-full z-50" : "relative"}`}
      >
        {pageName === "CAIRA" ? (
          <div className="main_container px-5 py-3 lg:py-3 flex items-center justify-between">
            <div className="flex-1">
              <p className="text-xs md:text-base text-white font-semibold">
                Become a Certified AI-Ready Accountant (CAIRA) Free CAIRA Level
                1 Live Webinars with your CPA/CMA at Miles
              </p>
            </div>
            <button
              onClick={handleFormModalOpen}
              className="text-xs md:text-sm bg-white text-black font-medium md:font-semibold md:px-6 px-2 py-2 rounded-[20px_20px_20px_0px] ml-4 whitespace-nowrap hover:bg-gray-100 transition-colors duration-200"
              aria-label="Sign up for free"
            >
              Sign up for Free
            </button>
          </div>
        ) : pageName === "MPACT2025" ? (
          <div className="main_container px-5 py-3 lg:py-3 flex items-center justify-between">
            <div className="flex-1">
              <p className="text-[10px] md:text-base lg:text-xl text-white font-semibold">
                Join us for{" "}
                <span>
                  <span className="text-[#39F]">
                    {" "}
                    Miles Public Accounting GCC Conclave
                  </span>
                </span>{" "}
                on 06 December
              </p>
            </div>
            <button
              onClick={handleFormModalOpen}
              className="absolute text-[10px] md:text-base bg-[#FFBA08] text-[#071B30] font-semibold px-5 py-1 lg:py-2 lg:px-10 rounded-l-full right-0 top-0 h-full max-w-[80px] md:max-w-full leading-tight hover:bg-[#e6a607] transition-colors duration-200"
              aria-label="Register for Miles Finance GCC Conclave"
            >
              Register Now
            </button>
          </div>
        ) : pageName === "CPA/CMA" ||
          pageName === "CMA" ||
          pageName === "CPA" ||
          pageName === "EA" ||
          pageName === "USP" ||
          pageName === "Accounting" ? (
          <div
            className={`main_container px-5 py-3 md:py-3 flex items-center gap-4 ${showLoginButton ? "justify-between" : "justify-center"}`}
          >
            {/* Left side - Login section (only on CPA/CMA pages) */}
            {showLoginButton && (
              <div className="hidden md:flex items-center gap-3">
                <p className="bg-gradient-to-r from-[#39F] to-[#FFF] bg-clip-text text-transparent text-xl">
                  Already enrolled? Log in to continue learning.
                </p>
                <Link
                  href={loginUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-12 py-1.5 border border-[#3399FF] text-[#3399FF] text-sm font-semibold rounded-[8px] transition-colors duration-200 whitespace-nowrap"
                >
                  Login
                </Link>
              </div>
            )}

            {/* Action buttons - Center aligned when no login button, right aligned when login button is present */}
            <div
              className={`flex items-center gap-4 ${showLoginButton ? "justify-center md:justify-end flex-1" : "justify-center"}`}
            >
              {/* First button - "Enquire Now" or "Join FREE Webinar" */}
              <button
                onClick={
                  shouldUseScrollFunctionality
                    ? handleScrollToLiveWebinarForm
                    : handleFormModalOpen
                }
                style={{ width: "152px", height: "32px" }}
                className="flex items-center justify-center gap-2 px-0 py-3 bg-[#2A85FF] text-white text-sm font-semibold rounded-[8px] hover:bg-[#1a75ef] transition-colors duration-200"
                aria-label={
                  shouldUseScrollFunctionality
                    ? "Join FREE Webinar"
                    : "Enquire Now"
                }
              >
                {shouldUseScrollFunctionality
                  ? "Join FREE Webinar"
                  : "Enquire Now"}
              </button>

              {/* Second button (Desktop) - "Register for Webinar" or "Talk to an Expert" */}
              <button
                onClick={
                  shouldUseScrollFunctionality
                    ? handleScrollToBannerForm
                    : handleWebinarModalOpen
                }
                style={{ width: "152px", height: "32px" }}
                className="hidden md:flex items-center justify-center gap-2 px-0 py-3 bg-[#FFBA08] text-black text-sm font-semibold rounded-[8px] hover:bg-[#e6a607] transition-colors duration-200"
                aria-label={
                  shouldUseScrollFunctionality
                    ? "Talk to an Expert"
                    : "Register for Webinar"
                }
              >
                {shouldUseScrollFunctionality
                  ? "Talk to an Expert"
                  : "Register for Webinar"}
              </button>

              {/* Second button (Mobile) - "Talk to an Expert" or "Request a Call" */}
              {shouldUseScrollFunctionality ? (
                <button
                  onClick={handleScrollToBannerForm}
                  style={{ width: "152px", height: "32px" }}
                  className="md:hidden flex items-center justify-center gap-2 px-0 py-3 bg-[#FFBA08] text-black text-sm font-semibold rounded-[8px] hover:bg-[#e6a607] transition-colors duration-200"
                  aria-label="Talk to an Expert"
                >
                  Talk to an Expert
                </button>
              ) : (
                <a
                  href="tel:+919513881313"
                  style={{ width: "152px", height: "32px" }}
                  className="md:hidden flex items-center justify-center gap-2 px-0 py-3 bg-[#FFBA08] text-black text-sm font-semibold rounded-[8px] hover:bg-[#e6a607] transition-colors duration-200"
                  aria-label="Talk to an Expert"
                >
                  Talk to an Expert
                </a>
              )}
            </div>
          </div>
        ) : null}
      </div>
      {isFormModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-auto mx-auto border border-white p-4 w-full max-w-[430px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleFormModalClose}
              className="text-white text-md md:text-lg absolute right-0 md:right-1 top-0 md:top-1 cursor-pointer z-30"
              aria-label="Close modal"
            >
              ✕
            </button>
            <div className="relative">
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
                alt="Decorative stars"
                width={34}
                height={38}
                className="absolute top-[-8px] left-[-24px] z-20"
                loading="lazy"
              />
              {pageName === "CAIRA" ? (
                <EnqueryForm
                  course="CAIRA"
                  courseId={18}
                  formHeading={FormHeading.WEEKLY_LIVE_WEBINAR_WITH_VARUN_JAIN}
                  formType="page"
                  isAStepperForm
                  subCategory={FinanceAndAccountingSubcategory.CPA}
                  submitButtonName="Request a call back"
                  successMessage={SuccessMessageType.ACCOUNTING}
                  verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                  darkBackground
                />
              ) : pageName === "MPACT2025" ? (
                <>
                  <h2 className="text-white md:text-xl text-center font-semibold pb-4 px-4">
                    Register for The Miles Public Accounting GCC Conclave
                  </h2>
                  <GCCForm submitButtonName="Enquire Now" darkBackground />
                </>
              ) : isAccountingCourse ? (
                <EnqueryForm
                  course={pageName || detectedCourse}
                  courseId={
                    pageName === "CPA"
                      ? 1
                      : pageName === "CMA"
                        ? 2
                        : pageName === "USP"
                          ? 3
                          : pageName === "EA"
                            ? 19
                            : detectedCourseId
                  }
                  formHeading={FormHeading.WEEKLY_LIVE_WEBINAR_WITH_VARUN_JAIN}
                  formType="page"
                  isAStepperForm
                  subCategory={
                    pageName === "CPA"
                      ? FinanceAndAccountingSubcategory.CPA
                      : pageName === "CMA"
                        ? FinanceAndAccountingSubcategory.CMA
                        : pageName === "USP"
                          ? FinanceAndAccountingSubcategory.USP
                          : pageName === "EA"
                            ? FinanceAndAccountingSubcategory.EA
                            : detectedCourse === "CAIRA"
                              ? FinanceAndAccountingSubcategory.CPA
                              : FinanceAndAccountingSubcategory.CPA
                  }
                  submitButtonName="Request a call back"
                  successMessage={SuccessMessageType.ACCOUNTING}
                  verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                  darkBackground={true}
                />
              ) : (
                <NursingForm
                  submitButtonName="Enquire Now"
                  darkBackground={true}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {isWebinarModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-auto mx-auto border border-white p-4 w-full max-w-[400px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleWebinarModalClose}
              className="text-white text-lg absolute right-1 top-1 cursor-pointer z-30"
              aria-label="Close modal"
            >
              ✕
            </button>
            <div className="relative">
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
                alt="Decorative stars"
                width={34}
                height={38}
                className="absolute top-[-8px] left-[-24px] z-20"
                loading="lazy"
              />
              <GlobalForm
                course={pageName}
                courseId={
                  pageName === "CPA"
                    ? 1
                    : pageName === "CMA"
                      ? 2
                      : pageName === "USP"
                        ? 3
                        : pageName === "EA"
                          ? 19
                          : 1
                }
                formHeading={FormHeading.JOIN_US_LIVE_WITH_VARUN_JAIN}
                formType="page"
                isAStepperForm
                pageType="accounting page"
                submitButtonName="Register for Webinar"
                successMessage={SuccessMessageType.LIVE_WEBINAR}
                showSVG={false}
                darkBackground={true}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StickyFooterLabel;
